// MARK://dhk8/pos/features/tour/useGuidedTour
// State machine for the onboarding coachmarks. The wizard redirects into the POS
// with `?tour=1`; this hook picks that up (once — it also persists "tour seen"
// so a reload / re-entry doesn't re-show it), walks a step index, and on
// finish/skip strips `?tour=1` from the URL + marks it seen.
//
// Pure logic, no DOM/JSX — <GuidedTour> consumes it. SSR/jsdom-safe.

import { useCallback, useEffect, useState } from 'react'

const SEEN_LS_KEY = 'dhk8_pos_tour_seen'

/** Has the user already seen (finished or skipped) the guided tour? */
export function tourSeen(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(SEEN_LS_KEY) === '1'
  } catch {
    return false
  }
}

function markSeen(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SEEN_LS_KEY, '1')
  } catch {
    /* ignore */
  }
}

/** True iff `?tour=1` is present in the current URL. */
function tourRequested(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return new URLSearchParams(window.location.search).get('tour') === '1'
  } catch {
    return false
  }
}

/** Drop `?tour=1` from the URL without a navigation (keeps the rest of the query). */
function clearTourParam(): void {
  if (typeof window === 'undefined') return
  try {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('tour')) return
    url.searchParams.delete('tour')
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
  } catch {
    /* ignore */
  }
}

export interface GuidedTourController {
  /** Whether the tour overlay should be mounted/visible. */
  active: boolean
  /** Zero-based index of the current step. */
  index: number
  /** Total number of steps. */
  count: number
  /** Advance to the next step, or finish on the last one. */
  next: () => void
  /** Go to the previous step (no-op on the first). */
  prev: () => void
  /** Skip the rest of the tour (counts as "seen"). */
  skip: () => void
  /** Finish the tour (counts as "seen"). */
  finish: () => void
}

/**
 * @param stepCount  number of steps in the tour (the caller owns the content).
 * @param force      if true, start the tour even without `?tour=1` (and even if
 *                   already seen) — for an in-app "ver tutorial" affordance.
 */
export function useGuidedTour(stepCount: number, force = false): GuidedTourController {
  const [active, setActive] = useState<boolean>(() => {
    if (stepCount <= 0) return false
    if (force) return true
    return tourRequested() && !tourSeen()
  })
  const [index, setIndex] = useState(0)

  // If the tour was requested via the URL, drop the param immediately so a
  // refresh / back-button doesn't re-trigger it (we keep showing it via state).
  useEffect(() => {
    if (active) clearTourParam()
  }, [active])

  const end = useCallback(() => {
    markSeen()
    clearTourParam()
    setActive(false)
  }, [])

  const next = useCallback(() => {
    setIndex((i) => {
      if (i >= stepCount - 1) {
        end()
        return i
      }
      return i + 1
    })
  }, [stepCount, end])

  const prev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : 0))
  }, [])

  return {
    active,
    index: Math.min(index, Math.max(0, stepCount - 1)),
    count: stepCount,
    next,
    prev,
    skip: end,
    finish: end,
  }
}
